<template>
  
  <div class="ipt-container">

    <div style="height: 60px;"></div>
    <div class="header">
      <div class="header-title">{{ info.title }}</div>
      <div class="companyName">基于问题的技术路径分析与方案生成</div>
    </div>

    <div class="footer">
      <div class="backgroundCss"> </div>
      <div class="footer-input">
        <ProblemUnderstanding
          v-model="searchValue"
          @start="handleStart"
          @quickStart="handleStart"
        >
          <template #actions-extra>
            <div class="voice-mic-wrap">
              <el-icon
                size="24"
                :class="{ 'is-recording': isRecording }"
                style="cursor: pointer;"
                @click="toggleVoiceDirect"
              ><Microphone /></el-icon>
              <!-- 录音中：麦克风下方的红色波形动画 -->
              <div v-if="isRecording" class="voice-wave">
                <span class="voice-wave__bar"></span>
                <span class="voice-wave__bar"></span>
                <span class="voice-wave__bar"></span>
                <span class="voice-wave__bar"></span>
                <span class="voice-wave__bar"></span>
              </div>
            </div>
          </template>
        </ProblemUnderstanding>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router';
import { Microphone } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { innovationAdd } from '../api/process';
import ProblemUnderstanding from '../components/ProblemUnderstanding.vue';
import WebAudioSpeechRecognizer from '@/utils/asr/webaudiospeechrecognizer'
import { WebSocketConnectMethod, WebSocketStopMethod } from '@/utils/wsconnecter'
import { audioPermission } from "@/utils/permission"

const statistData = ref({})
const companyName = ref('')
const options = [
];

const router = useRouter();
const isLoading = ref(false);
const params = ref({})
const searchValue = ref('')

// 语音输入相关
const isRecording = ref(false)
const recordingTime = ref(0)
const isProcessing = ref(false)
let recordingTimer = null
let recognizer = null
const isOnline = process.env.VUE_APP_VOICE_ONLINE === 'true'
const voiceWebsocket = isOnline ? null : WebSocketConnectMethod({ stateHandle }, process.env.VUE_APP_VIOCE_REALTIME)

const cards = []

const info = {
  title: '已就绪，随时可以开始',
}

const childOptions = computed(() => {
  return options.find(v => v.value === params.value.ipt)?.children || []
})

// 接收问题理解卡确认后的标准化对象，创建项目并进入分析页
const handleStart = (payload = {}) => {
  const raw = payload.raw_query || searchValue.value
  const standard = payload.standard_problem || raw
  if (!raw) {
    ElMessage.error('请输入问题描述')
    return
  }
  isLoading.value = true;
  const params = {
    projectName: raw,
    problemDescription: standard,
    projectStatus: 0,
    projectContent: JSON.stringify({
      problemUnderstanding: payload
    })
  }
  innovationAdd(params).then((res) => {
    isLoading.value = false;
    console.log(res)
    if (res) {
      const url = `/process?id=${res.id}&problemDescription=${encodeURIComponent(standard)}`;
      router.push(url);
    }
  });
};

// 点麦克风：直接开始/停止录音（不弹窗），识别文字实时写入输入框
const toggleVoiceDirect = () => {
  audioPermission().then(state => {
    if (state === "denied") {
      ElMessage.error('录音权限被拒绝，请在浏览器设置中允许麦克风访问')
    } else if (isRecording.value) {
      stopRecording()
    } else {
      startRecording()
    }
  });
}

// 格式化时间
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// 开始录音
const startRecording = async () => {
  isRecording.value = true
  recordingTime.value = 0
  
  // 开始计时
  recordingTimer = setInterval(() => {
    recordingTime.value++
  }, 1000)
  
  try {
    if (isOnline) {
      // 在线录音
      await startRecordingOnline()
    } else {
      // 离线录音
      await startRecordingOffline()
    }
  } catch (error) {
    console.error('录音启动失败:', error)
    ElMessage.error('录音启动失败：' + error.message)
    stopRecording()
  }
}

// 在线录音
const startRecordingOnline = async () => {
  try {
    const params = {
      secretid: process.env.VUE_APP_TENCENT_SECRET_ID,
      secretkey: process.env.VUE_APP_TENCENT_SECRET_KEY,
      appid: Number(process.env.VUE_APP_TENCENT_APPID),
      engine_model_type: '16k_zh',
      voice_format: 1,
      needvad: 1,
      filter_dirty: 0,
      filter_modal: 0,
      filter_punc: 0,
      convert_num_mode: 1,
      word_info: 0
    }

    recognizer = new WebAudioSpeechRecognizer(params, true)

    recognizer.OnRecognitionStart = () => {
      console.log('开始识别')
    }

    recognizer.OnRecognitionResultChange = (res) => {
      if (res.result?.voice_text_str) {
        // 识别文字实时写入输入框
        searchValue.value = res.result.voice_text_str
      }
    }

    recognizer.OnRecognitionComplete = () => {
      stopRecording()
    }

    recognizer.OnError = (error) => {
      console.error('识别错误:', error)
      ElMessage.error('识别错误：' + (error.message || '未知错误'))
      stopRecording()
    }

    await recognizer.start()

  } catch (error) {
    console.error('在线录音初始化失败:', error)
    throw error
  }
}

// 离线录音
const startRecordingOffline = async () => {
  try {
    if (!voiceWebsocket) {
      throw new Error('离线录音服务未初始化')
    }

    await voiceWebsocket.wsStart({
      onOpen: () => {
        console.log('离线录音连接成功')
      },
      onMessage: (jsonMsg) => {
        const contentText = JSON.parse(jsonMsg.data)['text'];
        if (contentText) {
          // 识别文字实时写入输入框
          searchValue.value += contentText
        }
      },
      onError: () => {
        console.error('离线录音连接失败')
        ElMessage.error('离线录音连接失败')
        stopRecording()
      }
    })
  } catch (error) {
    console.error('离线录音初始化失败:', error)
    throw error
  }
}

// 停止录音
const stopRecording = () => {
  isRecording.value = false
  
  // 清除计时器
  if (recordingTimer) {
    clearInterval(recordingTimer)
    recordingTimer = null
  }
  
  // 停止在线录音
  if (recognizer) {
    recognizer.stop()
    recognizer = null
  }
  
  // 停止离线录音
  if (voiceWebsocket && !isOnline) {
    WebSocketStopMethod(voiceWebsocket, false)
  }
}

// WebSocket状态处理
function stateHandle(connState) {
  if (connState === 0) {
    // 连接成功
    console.log('stateHandle: 0 连接成功')
  } else if (connState === 1) {
    // 连接断开
    console.log('stateHandle: 1 连接断开')
  } else if (connState === 2) {
    // 连接失败
    console.log('stateHandle: 2 连接失败')
    ElMessage.error('语音服务连接失败')
  }
}

watch(() => params.value.ipt, (newValue) => {
  const child = options.find(v => v.value === newValue)?.children
  params.value.iptChild = child?.[0]?.value
})

const handleClickItem = (item) => {
  if (item.url) {
    window.open(item.url)
  }
}

onMounted(() => {

})

onUnmounted(() => {
  if (recognizer) {
    recognizer.destroyStream()
    recognizer = null
  }
  if (recordingTimer) {
    clearInterval(recordingTimer)
  }
  if (voiceWebsocket && !isOnline) {
    WebSocketStopMethod(voiceWebsocket, false)
  }
})

</script>

<style scoped lang="scss">
.ipt-container {
  position: relative;
  display: flex;
  flex-direction: column;
  // justify-content: center; 
  gap: 40px;
  width: 100%;
  height: 100%;
  background-color: #F8F8F7;
}

.animation-wraper {
  position: fixed;
  top: 0;
  right: 0;
}

.header {
  position: relative;
  z-index: 10;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  .header-title {
    color: #333;
    line-height: 40px;
    font-size: 48px;
    font-weight: 500;
  }
  .companyName {
    color: #7B7B7B; /* 使用比标题浅一些的颜色 */
    line-height: 60px; /* 适当调整行高 */
    font-size: 20px; /* 比标题小一些的字体 */
    font-weight: 400; /* 正常字体粗细 */
  }
}
.backgroundCss{
  width: 800px;
  height: 240px;
  background: linear-gradient( 0deg, rgba(255,255,255,0) 0%, rgba(158,212,251,0.64) 82%, #000000 100%);
  border-radius: 120px;
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
}
.footer-input {
  position: relative;
  width: 100%;
  max-width: 900px;

  textarea {
    width: 100%;
  }
}

.ipt-textarea {
  width: 100%;
  resize: none;
  font-family: inherit;
  color: #34322E;
  background-color: transparent;
  border: none;
  padding: 10px;
  outline: transparent;
  
  &::placeholder {
    color: #34322E !important;
    opacity: 0.6;
  }
  
  &::-webkit-input-placeholder {
    color: #34322E !important;
    opacity: 0.6;
  }
  
  &:-moz-placeholder {
    color: #34322E !important;
    opacity: 0.6;
  }
  
  &::-moz-placeholder {
    color: #34322E !important;
    opacity: 0.6;
  }
  
  &:-ms-input-placeholder {
    color: #34322E !important;
    opacity: 0.6;
  }
  
  &::-ms-input-placeholder {
    color: #34322E !important;
    opacity: 0.6;
  }
}

.cards-container {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 0 11%;
}

.card {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 20vh;
  overflow: hidden;
  padding: 24px 30px;
  background: rgba(255, 255, 255, 0.6);
  box-shadow: 0px 4px 18px 2px rgba(71,84,77,0.14);
  border-radius: 30px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
}

.card:hover {
  opacity: 1;
  transform: translateY(-5px);
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient( 194deg, rgba(0,161,96,0.1) 0%, rgba(255,255,255,0) 100%);
  }
}

.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  font-weight: 400;
  font-size: 30px;
}

.card-content {
  flex: 1;
  margin-bottom: 20px;
  line-height: 30px;
  color: #999;
  font-size: 16px;
  em {
    margin-left: 20px;
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    color: #222;
  }
}

.history-btn,
.history-btn:hover {
  display: none;
}

.footer {
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
  z-index: 10;
}

.footer-btn {
  display: flex;
  align-items: center;
  position: absolute;
  right: 20px;
  bottom: 20px;
}

.analyze-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #34322E;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #222;
    color: #fff;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(52, 50, 46, 0.2);
  }
}

.ipt-textarea {
  padding: 20px !important;
  font-size: 20px;
}

@media screen and (max-width: 1660px) {
  .cards-container {
    padding: 0 5%;
  }
}

@media screen and (max-width: 1366px) {
  .header {
    .header-title {
      font-size: 36px;
    }
  }
  .cards-container {
    padding: 0 20px;
  }
  .card-footer-left {
    gap: 10px;
  }
}

/* 麦克风录音激活样式 */
.is-recording {
  color: #ff4d4f;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 麦克风 + 上方波形（麦克风在输入框内右下角，波形显示在其上方避免伸出框外） */
.voice-mic-wrap {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 4px;
}

.voice-wave {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 16px;

  &__bar {
    width: 3px;
    height: 100%;
    border-radius: 2px;
    background: #ff4d4f;
    animation: voice-bounce 1s infinite ease-in-out;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
    &:nth-child(4) { animation-delay: 0.45s; }
    &:nth-child(5) { animation-delay: 0.6s; }
  }
}

@keyframes voice-bounce {
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
}
</style>
